// src/App.jsx
import React, { useCallback, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import stores
import useAuthStore from './stores/authStore';
import useSettingsStore from './stores/settingsStore';

// Import theme
import createAghrbaTheme from './theme';

// Import router
import router from './router';
import ErrorBoundary from './components/ui/ErrorBoundary';
import errorHandlingService from './services/error/errorHandlingService';
import StackedNotifications from './components/ui/Feedback/StackedNotifications';

// Import Socket Context
import { SocketProvider } from './contexts/SocketContext';

// Import Socket Integration
import { initializeSocketIntegration, cleanupSocketIntegration } from './stores/socketIntegration';

// Import Performance Monitor (dev only)
// import PerformanceMonitor from './components/dev/PerformanceMonitor';

// Create React Query client with simplified error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: (error) => {
        // Log the error but don't throw it
        console.error('React Query error:', error);
        errorHandlingService.reportError(error, { source: 'react-query' });
      }
    },
    mutations: {
      onError: (error) => {
        // Log the error but don't throw it
        console.error('React Query mutation error:', error);
        errorHandlingService.reportError(error, { source: 'react-query-mutation' });
      }
    }
  },
});

// Global error handler as safety net for any unhandled errors
const setupGlobalErrorHandlers = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    // Prevent default browser behavior that would crash the app
    event.preventDefault();
    
    // Log the error
    console.error('Unhandled Promise Rejection (Safety Net):', event.reason);
    
    // Report to error service
    errorHandlingService.reportError(event.reason, { 
      source: 'unhandled_promise',
      message: 'Unhandled Promise Rejection'
    });
    
    return true; // Indicates error was handled
  });

  // Handle global errors
  window.addEventListener('error', (event) => {
    console.error('Global Error (Safety Net):', event.error || event.message);
    
    errorHandlingService.reportError(event.error || event.message, { 
      source: 'global_error',
      message: 'Uncaught JavaScript Error'
    });
  });
  
  console.log('Global error safety net installed');
};

// Root App wrapper with error boundary
const App = () => {
  // Set up global error handlers
  useEffect(() => {
    setupGlobalErrorHandlers();
  }, []);

  // Memoize the global error handler
  const globalErrorHandler = useCallback((error, errorInfo) => {
    errorHandlingService.reportError(error, {
      source: 'global_root',
      info: errorInfo
    });
    console.error('Root Error Boundary caught an error:', error, errorInfo);
  }, []);

  return (
    <ErrorBoundary 
      onError={globalErrorHandler}
      severity="critical"
      title="Critical Application Error"
      message="We encountered a serious error. Please refresh the page to continue."
    >
      <AppContent />
    </ErrorBoundary>
  );
};

// Main App component with its own error boundary
const AppContent = () => {
  const { initAuth, isLoading: authLoading } = useAuthStore();
  const { settings, initSettings } = useSettingsStore();

  // Initialize settings and auth
  // useEffect(() => {
  //   initSettings();
  //   initAuth();
  // }, [initSettings, initAuth]);
  
  // Create theme based on settings
  const theme = createAghrbaTheme(settings.theme, settings.direction, settings.colorPalette);
  
  // Memoize the app error handler
  const appErrorHandler = useCallback((error, errorInfo) => {
    errorHandlingService.reportError(error, {
      source: 'app_container',
      info: errorInfo
    });
    console.error('App Error Boundary caught an error:', error, errorInfo);
  }, []);

  useEffect(() => {
    // Set up error tracking listener
    const removeListener = errorHandlingService.addListener((error) => {
      console.log('Error captured by global handler:', error);
      // You could send this to an external error tracking service here
    });

    // Cleanup function to remove the listener when component unmounts
    return () => {
      removeListener();
    };
  }, []);

  // Initialize socket integration for real-time updates
  useEffect(() => {
    // Initialize socket listeners
    initializeSocketIntegration();

    // Cleanup function to remove socket listeners when component unmounts
    return () => {
      cleanupSocketIntegration();
    };
  }, []);
  
  // Show loading indicator while auth is being checked
  if (authLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <ErrorBoundary 
      onError={appErrorHandler}
      severity="critical"
      title="Application Error"
      message="We encountered an error in the application. Some features may not work correctly."
    >
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SocketProvider>
              <CssBaseline />
              {/* Global notification system - positioned outside overflow container for proper display */}
              <StackedNotifications />
              
              <Box sx={{ flexGrow: 1, overflow: 'auto', height: '100%' }}>
                <ErrorBoundary 
                  severity="warning"
                  title="Navigation Error"
                  message="We encountered an issue with this page. You can try again or navigate to another page."
                >
                  <RouterProvider router={router} />
                </ErrorBoundary>
              </Box>
              
              {/* Performance Monitor (Development Only) */}
              {/* <PerformanceMonitor /> */}
            </SocketProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>  
    </ErrorBoundary>
  );
};

export default App;